import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, map, Observable, of, retry, throwError, timer } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ConnMessageService } from './conn-message.service';
import { GlobalService } from './global.service';

@Injectable({
  providedIn: 'root'
})
export class BaseService {
  baseUrl: string = ''
  _globals = GlobalService
  isRedirect:any = null
  
  constructor(
    private http: HttpClient,
    private _connMessageService: ConnMessageService,
    private _spinner: NgxSpinnerService,
    // private toastr: ToastrService
  ) {
    this.baseUrl = environment.apiUrl
  }

  private buildHeaders(auth: boolean = false, businessName: string = null, extraHeaders: { [key: string]: string } = null): HttpHeaders {
    let headers: HttpHeaders;
    const baseHeaders = this.makeHttpHeader(auth);

    if (baseHeaders instanceof HttpHeaders) {
      headers = baseHeaders;
    } else {
      headers = new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8"
      });
    }

    if (businessName) {
      headers = headers.set('BusinessName', businessName);
    }

    if (extraHeaders) {
      for (const key of Object.keys(extraHeaders)) {
        headers = headers.set(key, extraHeaders[key]);
      }
    }

    return headers;
  }

  private withTransientRetry<T>(request$: Observable<T>): Observable<T> {
    return request$.pipe(
      retry({
        count: 2,
        delay: (error: any, retryCount: number) => {
          if (error?.status === 0 || error?.status === 502 || error?.status === 503 || error?.status === 504) {
            return timer(300 * retryCount);
          }
          throw error;
        }
      })
    );
  }

  private handleHttpError(error: any, showSpinner: boolean): Observable<any> {
    if (showSpinner) {
      this._spinner.hide();
    }

    const status = error?.status;
    if (status === 502 || status === 503 || status === 504) {
      this._connMessageService.showToastMessage('Server temporarily unavailable. Please try again in a moment.', 'warn');
    } else if (status === 0) {
      this._connMessageService.showToastMessage('Network error: unable to reach server.', 'warn');
    }

    // Keep message empty so `processErrors` doesn't return `undefined` for message-bearing failures.
    return of({ success: false, data: null, message: '', status });
  }

  httpPost(
    _url: string,
    model: any = null,
    auth: boolean = false,
    showSpinner: boolean = true,
    businessName: string = null,
    skipProcessErrors: boolean = false,
    extraHeaders: { [key: string]: string } = null
  ) {
    // if (showSpinner) this._abd.showSpinner();
    // else this._abd.hideSpinner();
    let headers = this.buildHeaders(auth, businessName, extraHeaders);
    // multipart uploads need the browser to set Content-Type (with the boundary)
    if (typeof FormData !== 'undefined' && model instanceof FormData) {
      headers = headers.delete('Content-Type');
    }
    return this.withTransientRetry(this.http.post(_url, model, { headers })).pipe(
      map((res: any) => {
        if (showSpinner) this._spinner.hide();
        return skipProcessErrors ? res : this.processErrors(res)
      }),
      catchError((error: any) => this.handleHttpError(error, showSpinner))
    )
  }

  httpPut(_url: string, model: any = null, auth: boolean = false, showSpinner: boolean = true, businessName:string=null) {
    // if (showSpinner) this._abd.showSpinner();
    // else this._abd.hideSpinner();
    const headers = this.buildHeaders(auth, businessName);
    return this.withTransientRetry(this.http.put(_url, model, { headers })).pipe(
    
      // catchError(this.handleError),
      map((res: any) => {
        if (showSpinner) this._spinner.hide();
        return this.processErrors(res);
      }),
      catchError((error: any) => this.handleHttpError(error, showSpinner))
    );
  }

  // handleError(error: HttpErrorResponse) {
  //   if (error.status === 500) {
  //     this._connMessageService.showToastMessage('The server was unable to complete your request. Please try again later.');
  //   }
  //   return throwError(() => error);
  // }

  httpGetParams(
    _url: string,
    model: any = null,
    auth: boolean = false,
    showSpinner: boolean = true,
    skipInterceptor: boolean = false,
    extraHeaders: { [key: string]: string } = null
  ): Observable<any> {
    // if (showSpinner) this._abd.showSpinner()
    // else this._abd.hideSpinner();
    let headers = this.buildHeaders(auth, null, extraHeaders);
    if (skipInterceptor) {
      headers = headers.set('do-need-to-process-interceptor', 'skip');
    }
    return this.withTransientRetry(
      this.http.get(_url, { params: model, headers })
    ).pipe(map(res => {
      // if (showSpinner) this._abd.hideSpinner();
      return this.processErrors(res)
    }),
    catchError((error: any) => this.handleHttpError(error, showSpinner)));
  }

  httpGet(
    _url: string,
    showSpinner: boolean = true,
    businessName: string = null,
    skipInterceptor: boolean = false
  ): Observable<any> {
    // if (showSpinner) this._abd.showSpinner();
    // else this._abd.hideSpinner();
    let headers = this.buildHeaders(false, businessName);
    if (skipInterceptor) {
      headers = headers.set('do-need-to-process-interceptor', 'skip');
    }
    return this.withTransientRetry(this.http.get(_url, { headers })).pipe(map(res => {
      // if (showSpinner) this._abd.hideSpinner();
      return this.processErrors(res)
    }),
    catchError((error: any) => this.handleHttpError(error, showSpinner)));
  }
  httpGetByPassInterceptor(_url: string, showSpinner: boolean = true): Observable<any> {
    // if (showSpinner) this._abd.showSpinner();
    // else this._abd.hideSpinner();

    return this.http.get(_url).pipe(map(res => {
      // if (showSpinner) this._abd.hideSpinner();
      return res;
    }));
  }

  httpDelete(_url: string,businessName:string=null): Observable<any> {
    const headers = this.buildHeaders(false, businessName);
    return this.withTransientRetry(this.http.delete(_url, { headers })).pipe(
      map(res => this.processErrors(res)),
      catchError((error: any) => this.handleHttpError(error, true))
    )
  }


  //--------------------------------------------------
  makeHttpHeader(auth: boolean) {
    let wlid = <any>this._globals.getWlId()
    if (!wlid) return false

    if (auth) {
      return new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8",
        WLID: wlid,
        Authorization: `Bearer ${this._globals.getUserToken()}`
      })
    } else {
      return new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8",
        WLID: wlid
      })
    }
  }
  //--------------------------------------------------
  GetAuthHeader() {
    let wlid = <any>this._globals.getWlId()
    if (!wlid) return null


    return new HttpHeaders({
      "Content-Type": "application/json; charset=utf-8",
      WLID: wlid,
      Authorization: `Bearer ${this._globals.getUserToken()}`
    })

  }

  processErrors(res: any) {
    // this._abd.hideSpinner()
    //
    if (res.success) {
      try {
        
        if (res.data.accountStatusString === 'AccessDenied') {
          let errorMessage = 'Access denied, invalid username or password'
          res.success = false
          this._globals.loginErrorMessage = errorMessage;
          this._connMessageService.showToastMessage(errorMessage)
        }
        else if (res.data.accountStatusString === 'InvalidAccount') {
          let errorMessage = 'Access denied, invalid username or password'
          res.success = false
          this._globals.loginErrorMessage = errorMessage;
          this._connMessageService.showToastMessage(errorMessage)
        }
        else if (res.data.accountStatusString === 'DeactivatedAccount') {
          let errorMessage = 'You have not activated your account yet. Please check your email to activate your account.'
          res.success = false
          this._globals.loginErrorMessage = errorMessage;
          this._connMessageService.showToastMessage(errorMessage)
        }
        else if (res.data.accountStatusString === 'DeploymentInProgress') {
          let errorMessage = 'Your Business account setup is in progress. Please wait for confirmation email.'
          res.success = false
          this._globals.loginErrorMessage = errorMessage;
          this._connMessageService.showToastMessage(errorMessage)
        }
        else if (res.data.accountStatusString === 'AccountExpired') {
          let errorMessage = 'Your Account is expired, please contact support.'
          res.success = false
          this._globals.loginErrorMessage = errorMessage;
          this._connMessageService.showToastMessage(errorMessage)
        }

      } catch { }
    } else {
      if (res.message === "Activation link has been expired!") {
        this._globals.loginErrorMessage = res.message;
      }
      else if (res.message === "") {
        this._globals.loginErrorMessage = res.message;
      }
      else if (res.message === "Invalid Link!") {
        this._globals.loginErrorMessage = res.message;
      }
      // else if (res.message === 'Shipping Zone Updated Successfully!' ) {
      //   res.success = true
      //   return res
      // }
      else if (res.message) {
        this._connMessageService.showToastMessage(res.message)
        return
      }
     // this._connMessageService.showToastMessage(res)
    }
    return res
  }



}


